import { useCallback, useEffect, useState, useRef } from "react";
import { useParams } from "react-router-dom";
import { Unity, useUnityContext } from "react-unity-webgl";

import useRefreshToken from "../../../hooks/useRefreshToken";
import { useAuth } from "../../../hooks/useAuth";
import { axiosPrivate } from "../../../api/axios";
import API_URL from "../../../utils/API_URL";

import SideBar from "./SVSidebar";
import SimulationChart from "../SimulationChart";
import SimulationParameterPopup from "./SimulationParameterPopup";
import SimulationResultPopup from "./SimulationResultPopup";

import { useDispatch, useSelector } from "react-redux";
import { setChattingStatus } from "../../../slice/authSlice";

function SVPage() {
  useEffect(() => {
    document.title = "시뮬레이션";
  }, []);
  const auth = useAuth();
  const { group_code, project_code } = useParams();
  const { svResultStatus } = useSelector((state) => state.auth); // Redux 상태 가져오기
  const {
    unityProvider,
    unload,
    sendMessage,
    loadingProgression,
  } = useUnityContext({
    loaderUrl: "https://preview.insightwin.com/Build/WebGL.loader.js",
    dataUrl: "https://preview.insightwin.com/Build/WebGL.data.gz",
    frameworkUrl: "https://preview.insightwin.com/Build/WebGL.framework.js.gz",
    codeUrl: "https://preview.insightwin.com/Build/WebGL.wasm.gz",
    streamingAssetsUrl: `https://preview.insightwin.com/StreamingAssets/${group_code}/${project_code}/sv`,
  });
  const dispatch = useDispatch();
  const unityContainerRef = useRef(null);
  const resizeFrameRef = useRef(null);
  const [devicePixelRatio, setDevicePixelRatio] = useState(
    window.devicePixelRatio
  );
  const [isLoading, setIsLoading] = useState(true);
  const [isTailwindReady, setIsTailwindReady] = useState(false);
  const [isSimulationPopupOpen, setIsSimulationPopupOpen] = useState(false);
  const [isResultPopupOpen, setIsResultPopupOpen] = useState(false);

  useEffect(() => {
    const existingScript = document.getElementById("tailwind-cdn-script");
    if (existingScript) {
      setTimeout(() => setIsTailwindReady(true), 100);
      return undefined;
    }

    const script = document.createElement("script");
    script.src = "https://cdn.tailwindcss.com";
    script.id = "tailwind-cdn-script";
    script.onload = () => {
      setTimeout(() => setIsTailwindReady(true), 100);
    };
    document.head.appendChild(script);

    return () => {
      const mountedScript = document.getElementById("tailwind-cdn-script");
      if (mountedScript) mountedScript.remove();

      const tailwindStyles = document.querySelectorAll("style");
      tailwindStyles.forEach((style) => {
        if (
          style.textContent.includes("--tw-") ||
          style.textContent.includes("tailwindcss")
        ) {
          style.remove();
        }
      });
    };
  }, []);

  const SFV_FinishLoading = useCallback((msg) => {
    console.log(msg);
    if (msg === "Initialization start") {
      setIsLoading(false);
    }
  }, []);

  const SFV_UpdateData = useCallback(
    (mchId, pickingData) => {
      console.log("MchID:", mchId, "PickingData:", pickingData);

      if (mchId === "1" && pickingData.includes('"type":0')) {
        navigator.mediaDevices
          .getUserMedia({ audio: true })
          .then(() => {
            console.log("마이크 권한 승인");
            sendMessage("Canvas", "CurrentMicrophoneStatus", "Enable");
          })
          .catch((error) => {
            console.error("마이크 권한 획득 실패: ", error);
            sendMessage("Canvas", "CurrentMicrophoneStatus", error.message);
          });
      }
    },
    [sendMessage]
  );

  const SFV_GetSimList = useCallback(async () => {
    try {
      // API_URL(https://dev.insightwin.com/backend)에서 /backend 제거하여 루트 도메인 추출
      const rootUrl = API_URL.replace("/backend", "");
      
      const response = await axiosPrivate.get(
        `/StreamingAssets/${group_code}/${project_code}/sv/Api/SimInfo`,
        {
          baseURL: rootUrl // baseURL 재정의
        }
      );

      // axios는 response.data에 JSON 결과가 들어있음. response.ok 체크 불필요(에러시 catch로 감)
      const data = response.data;
      console.log("SimInfo List:", data.simInfo);
      
      // Unity로 리스트 전달 (JSON 문자열)
      // Unity측 수신 함수명: "ReceiveSimList" (가정, 필요시 수정)
      sendMessage("Canvas", "ReceiveSimList", JSON.stringify(data));
      
    } catch (error) {
      console.error("SFV_GetSimList Error:", error);
      sendMessage("Canvas", "ReceiveSimList", JSON.stringify({ error: error.message }));
    }
  }, [group_code, project_code, sendMessage]);

  const SFV_UpdateStatus = useCallback((type, status) => {
    dispatch(
      setChattingStatus({
        type,
        status,
      })
    );
  }, [dispatch]);

  const updateCanvasSize = useCallback(() => {
    const unityContainer = unityContainerRef.current;
    if (unityContainer) {
      const canvas = unityContainer.querySelector("canvas");
      if (canvas) {
        const newWidth = unityContainer.clientWidth;
        const newHeight = unityContainer.clientHeight;
        const resolutionWidth = newWidth * window.devicePixelRatio;
        const resolutionHeight = newHeight * window.devicePixelRatio;
        const nextWidth = Math.max(1, Math.floor(resolutionWidth));
        const nextHeight = Math.max(1, Math.floor(resolutionHeight));

        if (canvas.width === nextWidth && canvas.height === nextHeight) {
          return;
        }

        // 유니티 WebGL 내부 해상도 업데이트
        canvas.width = nextWidth;
        canvas.height = nextHeight;
      }
    }
  }, []);

  const scheduleCanvasResize = useCallback(() => {
    if (resizeFrameRef.current !== null) {
      return;
    }

    resizeFrameRef.current = window.requestAnimationFrame(() => {
      resizeFrameRef.current = null;
      updateCanvasSize();
    });
  }, [updateCanvasSize]);

  const handleLogout = async () => {
    await unload();
    await auth.logout();
  };

  const handleSimulationPopupOpen = () => {
    setIsResultPopupOpen(false);
    setIsSimulationPopupOpen(true);
  };

  const handleSimulationPopupClose = () => {
    setIsSimulationPopupOpen(false);
  };

  const handleResultPopupOpen = () => {
    setIsSimulationPopupOpen(false);
    setIsResultPopupOpen(true);
  };

  const handleResultPopupClose = () => {
    setIsResultPopupOpen(false);
  };

  // Refresh Token 갱신
  const refresh = useRefreshToken();

  // Refresh Token 갱신
  useEffect(() => {
    let refreshInterval;

    const setupRefreshInterval = () => {
      if (refreshInterval) {
        console.log("Clearing existing interval");
        clearInterval(refreshInterval);
      }

      refreshInterval = setInterval(async () => {
        try {
          console.log("토큰 갱신중");
          await refresh(); // Refresh Token 갱신
          console.log("토큰 갱신 완료");
        } catch (error) {
          console.error("Failed to refresh tokens:", error);
          await unload();
          await auth.logout();
        }
      }, 12 * 60 * 60 * 1000);
    };

    setupRefreshInterval();

    return () => {
      console.log("Clearing interval on cleanup");
      clearInterval(refreshInterval); // 컴포넌트 언마운트 시 정리
    };
  }, [refresh, auth, unload]);

  useEffect(() => {
    const unityContainer = unityContainerRef.current;

    const handleDevicePixelRatioChange = () => {
      // console.log("Device Pixel Ratio:", window.devicePixelRatio);
      setDevicePixelRatio(window.devicePixelRatio);
      scheduleCanvasResize();
    };

    const mediaMatcher = window.matchMedia(
      `screen and (resolution: ${window.devicePixelRatio}dppx)`
    );
    mediaMatcher.addEventListener("change", handleDevicePixelRatioChange);

    scheduleCanvasResize();

    const observer =
      typeof ResizeObserver !== "undefined"
        ? new ResizeObserver(() => {
            scheduleCanvasResize();
          })
        : null;

    if (unityContainer && observer) {
      observer.observe(unityContainer);
    }

    return () => {
      mediaMatcher.removeEventListener("change", handleDevicePixelRatioChange);
      if (unityContainer && observer) {
        observer.unobserve(unityContainer);
      }

      if (resizeFrameRef.current !== null) {
        window.cancelAnimationFrame(resizeFrameRef.current);
        resizeFrameRef.current = null;
      }
    };
  }, [devicePixelRatio, scheduleCanvasResize]);

  useEffect(() => {
    window.SFV_UpdateData = SFV_UpdateData;
    window.SFV_UpdateStatus = SFV_UpdateStatus;
    window.SFV_FinishLoading = SFV_FinishLoading;
    window.SFV_GetSimList = SFV_GetSimList;
    return () => {
      delete window.SFV_UpdateData;
      delete window.SFV_UpdateStatus;
      delete window.SFV_FinishLoading;
      delete window.SFV_GetSimList;
    };
  }, [SFV_UpdateData, SFV_UpdateStatus, SFV_FinishLoading, SFV_GetSimList]);

  useEffect(() => {
    const preventDefault = (event) => {
      event.preventDefault();
    };

    const bodyElement = document.body;

    bodyElement.addEventListener("contextmenu", preventDefault);
    bodyElement.addEventListener("dragstart", preventDefault);
    bodyElement.addEventListener("selectstart", preventDefault);

    return () => {
      bodyElement.removeEventListener("contextmenu", preventDefault);
      bodyElement.removeEventListener("dragstart", preventDefault);
      bodyElement.removeEventListener("selectstart", preventDefault);
    };
  }, []);

  useEffect(() => {
    const isAnyPopupOpen = isSimulationPopupOpen || isResultPopupOpen;

    try {
      sendMessage(
        "Canvas",
        "SetCaptureAllKeyboardInput",
        isAnyPopupOpen ? "false" : "true"
      );
    } catch (error) {
      console.warn("Unity keyboard capture 변경 실패:", error);
    }
  }, [isResultPopupOpen, isSimulationPopupOpen, sendMessage]);

  const loadingPercent = Math.round(loadingProgression * 100);

  return (
    <div className="relative flex h-screen w-full overflow-hidden bg-black font-['Pretendard']">
      <div className="shrink-0">
        <SideBar
          handleLogout={handleLogout}
          onOpenSimulationSettings={handleSimulationPopupOpen}
          isSimulationSettingsOpen={isSimulationPopupOpen}
          onOpenResultPopup={handleResultPopupOpen}
          isResultPopupOpen={isResultPopupOpen}
        />
      </div>
      <div ref={unityContainerRef} className="relative flex-1 overflow-hidden">
        <Unity
          unityProvider={unityProvider}
          style={{ width: "100%", height: "100%" }}
          // 중요한 옵션, 디폴트가 true인데 WebGL 해상도를 캔버스 사이즈에 강제로 일치시킴
          matchWebGLToCanvasSize={false}
        />

        {(isLoading || !isTailwindReady) && (
          <div
            style={{
              position: 'absolute',
              inset: 0,
              zIndex: 9999,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              background: 'linear-gradient(135deg, #0a0c10 0%, #12151c 50%, #0d1017 100%)',
              overflow: 'hidden',
            }}
          >
            {/* 배경 그리드 패턴 */}
            <div
              style={{
                position: 'absolute',
                inset: 0,
                backgroundImage: `
                linear-gradient(rgba(115,103,240,0.03) 1px, transparent 1px),
                linear-gradient(90deg, rgba(115,103,240,0.03) 1px, transparent 1px)
              `,
                backgroundSize: '40px 40px',
                opacity: 0.8,
              }}
            />

            {/* 글로우 효과 */}
            <div
              style={{
                position: 'absolute',
                width: '600px',
                height: '600px',
                background: 'radial-gradient(circle, rgba(115,103,240,0.12) 0%, transparent 70%)',
                borderRadius: '50%',
                filter: 'blur(60px)',
                animation: 'pulse 4s ease-in-out infinite',
              }}
            />

            {/* 로고 영역 */}
            <div style={{ position: 'relative', marginBottom: '48px', textAlign: 'center' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '16px', marginBottom: '12px' }}>
                <img src="/images/ese_logo.png" alt="ESE-TECH" style={{ width: '56px', height: 'auto', filter: 'drop-shadow(0 0 20px rgba(115,103,240,0.4))' }} />
                <span style={{
                  fontSize: '42px',
                  fontWeight: 800,
                  color: '#E1DEF5',
                  letterSpacing: '-0.02em',
                  textShadow: '0 0 40px rgba(115,103,240,0.3)',
                }}>
                  InsighTwin
                </span>
              </div>
              <p style={{
                color: 'rgba(225,222,245,0.5)',
                fontSize: '13px',
                fontWeight: 500,
                letterSpacing: '0.15em',
                textTransform: 'uppercase',
              }}>
                Digital Twin Platform
              </p>
            </div>

            {/* 프로그레스 바 컨테이너 */}
            <div style={{ position: 'relative', width: '320px' }}>
              {/* 프로그레스 바 배경 */}
              <div
                style={{
                  height: '6px',
                  background: 'rgba(47,51,73,0.6)',
                  borderRadius: '3px',
                  overflow: 'hidden',
                  boxShadow: 'inset 0 1px 3px rgba(0,0,0,0.3)',
                }}
              >
                {/* 프로그레스 바 */}
                <div
                  style={{
                    width: `${loadingPercent}%`,
                    height: '100%',
                    background: 'linear-gradient(90deg, #7367F0 0%, #9E95F5 50%, #7367F0 100%)',
                    backgroundSize: '200% 100%',
                    borderRadius: '3px',
                    transition: 'width 0.3s ease-out',
                    boxShadow: '0 0 20px rgba(115,103,240,0.5), 0 0 40px rgba(115,103,240,0.2)',
                    animation: 'shimmer 2s linear infinite',
                  }}
                />
              </div>

              {/* 퍼센트 표시 */}
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginTop: '16px',
              }}>
                <span style={{
                  color: 'rgba(225,222,245,0.4)',
                  fontSize: '11px',
                  fontFamily: '"JetBrains Mono", "SF Mono", Consolas, monospace',
                  letterSpacing: '0.05em',
                }}>
                  LOADING ASSETS
                </span>
                <span style={{
                  color: '#7367F0',
                  fontSize: '14px',
                  fontWeight: 700,
                  fontFamily: '"JetBrains Mono", "SF Mono", Consolas, monospace',
                  textShadow: '0 0 10px rgba(115,103,240,0.5)',
                }}>
                  {loadingPercent}%
                </span>
              </div>
            </div>

            {/* CSS 애니메이션 */}
            <style>{`
            @keyframes pulse {
              0%, 100% { transform: scale(1); opacity: 0.5; }
              50% { transform: scale(1.1); opacity: 0.8; }
            }
            @keyframes shimmer {
              0% { background-position: 200% 0; }
              100% { background-position: -200% 0; }
            }
          `}</style>
          </div>
        )}

        <div className="pointer-events-none absolute inset-y-0 left-0 z-30 w-px bg-slate-800/70" />
        {svResultStatus && <SimulationChart sendMessage={sendMessage} />}
      </div>

      <SimulationParameterPopup
        open={isSimulationPopupOpen}
        onClose={handleSimulationPopupClose}
        groupCode={group_code}
        projectCode={project_code}
        onExecuteRequested={(result) => {
          sendMessage("Canvas", "ReceiveSimExecuteRequest", JSON.stringify(result));
        }}
      />

      <SimulationResultPopup
        open={isResultPopupOpen}
        onClose={handleResultPopupClose}
        groupCode={group_code}
        projectCode={project_code}
      />
    </div>
  );
}

export default SVPage;
